// Credit control (CA-O1, ADR-0047): the conservative automatic cut. Every figure is computed by the
// database (platform.credit_position) from the ad server's billable ledger × CPM, the purchase order
// reservations and the registered payments; the browser only renders it. A hold is placed automatically
// and never released automatically: only a SUPER_ADMIN releases one, once its condition is gone.
import { useState, type SyntheticEvent } from "react";
import { toast } from "sonner";
import { ApiProblem } from "@/api/errors";
import { Badge, type BadgeTone } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Field } from "@/ui/field";
import { Input } from "@/ui/input";
import { Callout, EmptyState, Section, Table, Td, Th } from "@/ui/layout";
import { formatDateTime } from "@/lib/format";
import { formField, ruleMessage, usd } from "../commercial/labels";
import { adminPost, useAdmin, useAdminMutation } from "./admin-api";
import { useOperatorRoles } from "./admin-layout";

interface Policy {
  real_credit_enabled: boolean;
  real_billing_enabled: boolean;
  org_safety_margin_bps: number;
  campaign_safety_margin_bps: number;
  warning_threshold_bps: number;
  consumption_max_age_minutes: number;
  updated_at: string;
}
interface Position {
  organizationId: string;
  company: string;
  isTest: boolean;
  limitMicros: number;
  consumedMicros: number;
  paidMicros: number;
  outstandingMicros: number;
  reservedUnconsumedMicros: number;
  committedMicros: number;
  availableMicros: number;
  cutThresholdMicros: number;
  state: "OK" | "WARNING" | "EXHAUSTED" | "HELD";
  consumptionStale: boolean;
  lastMeasuredAt: string | null;
}
interface Hold {
  id: string;
  company: string;
  scope: string;
  campaign: string | null;
  source: string;
  reason: string;
  createdAt: string;
}
interface CreditControl {
  policy: Policy;
  positions: Position[];
  holds: Hold[];
}

export const CREDIT_STATE: Record<Position["state"], { label: string; tone: BadgeTone }> = {
  OK: { label: "En regla", tone: "success" },
  WARNING: { label: "Próximo a agotarse", tone: "warning" },
  EXHAUSTED: { label: "Agotado", tone: "danger" },
  HELD: { label: "Detenida por crédito", tone: "danger" },
};
const HOLD_SOURCE: Record<string, string> = {
  credit_limit: "Crédito de la empresa agotado",
  credit_control: "Presupuesto de campaña agotado (automático)",
  budget_exhausted: "Presupuesto de campaña agotado (manual)",
};
const problemText = (e: unknown) => (e instanceof ApiProblem ? ruleMessage(e) : "No se pudo completar la acción.");
const pct = (bps: number) => `${String(bps / 100)} %`;

export function CreditControlPanel() {
  const roles = useOperatorRoles();
  const isSuper = roles.includes("SUPER_ADMIN");
  const { data, isLoading } = useAdmin<CreditControl>("/admin/credit-control");
  const run = useAdminMutation(() => adminPost.creditControlRun());
  if (isLoading || !data) return null;
  const p = data.policy;
  return (
    <Section
      title="Control de crédito"
      description="Corte conservador: cada campaña se detiene al llegar a su presupuesto menos el margen, y la empresa al llegar a su crédito menos el margen. Los holds se colocan solos y solo Super Admin los libera."
      actions={
        <Button
          variant="secondary"
          disabled={run.isPending}
          onClick={() => {
            run.mutate(undefined, {
              onSuccess: (r) => {
                const result = r as {
                  available: boolean;
                  readings: number;
                  actions: unknown[];
                  unpriced: unknown[];
                  failedOrganizations: unknown[];
                };
                if (!result.available) toast.error("El Ad Server no respondió: no se registró ninguna lectura.");
                else if (result.failedOrganizations.length > 0)
                  toast.warning(
                    `Medimos ${String(result.readings)} campañas · ${String(result.actions.length)} acciones. ` +
                      `${String(result.failedOrganizations.length)} empresas no se pudieron medir: conservan su lectura anterior.`
                  );
                else
                  toast.success(
                    `Medimos ${String(result.readings)} campañas · ${String(result.actions.length)} acciones` +
                      (result.unpriced.length ? ` · ${String(result.unpriced.length)} sin CPM vigente` : "")
                  );
              },
              onError: (e) => toast.error(problemText(e)),
            });
          }}
        >
          {run.isPending ? "Midiendo…" : "Medir consumo ahora"}
        </Button>
      }
    >
      <div className="grid gap-4">
        {!p.real_credit_enabled ? (
          <Callout title="Crédito real deshabilitado">
            Solo las empresas marcadas como datos de prueba pueden recibir crédito y órdenes aprobadas. Activarlo exige
            una decisión de política comercial del owner (CA-O1) y el procedimiento de operaciones.
          </Callout>
        ) : null}
        <p className="text-sm text-muted">
          Margen de la empresa {pct(p.org_safety_margin_bps)} · margen por campaña {pct(p.campaign_safety_margin_bps)} ·
          aviso al {pct(p.warning_threshold_bps)} · lectura vigente {String(p.consumption_max_age_minutes)} min
        </p>
        {isSuper ? <PolicyForm policy={p} /> : null}
        {data.positions.length === 0 ? (
          <EmptyState title="Ninguna empresa tiene condiciones comerciales" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>Empresa</Th>
                  <Th>Estado</Th>
                  <Th className="text-right">Crédito</Th>
                  <Th className="text-right">Consumido</Th>
                  <Th className="text-right">Pagado</Th>
                  <Th className="text-right">Pendiente</Th>
                  <Th className="text-right">Reservado sin consumir</Th>
                  <Th className="text-right">Disponible</Th>
                  <Th>Última lectura</Th>
                </tr>
              </thead>
              <tbody>
                {data.positions.map((x) => (
                  <tr key={x.organizationId}>
                    <Td>
                      {x.company}
                      {x.isTest ? <span className="ml-1 text-xs text-muted">(prueba)</span> : null}
                    </Td>
                    <Td>
                      <Badge tone={CREDIT_STATE[x.state].tone}>{CREDIT_STATE[x.state].label}</Badge>
                    </Td>
                    <Td className="text-right">{usd(x.limitMicros)}</Td>
                    <Td className="text-right">{usd(x.consumedMicros)}</Td>
                    <Td className="text-right">{usd(x.paidMicros)}</Td>
                    <Td className="text-right">{usd(x.outstandingMicros)}</Td>
                    <Td className="text-right">{usd(x.reservedUnconsumedMicros)}</Td>
                    <Td className="text-right">{usd(x.availableMicros)}</Td>
                    <Td className="text-xs">
                      {x.lastMeasuredAt ? formatDateTime(x.lastMeasuredAt) : "Sin lectura"}
                      {x.consumptionStale ? <span className="block text-warning">Lectura vencida</span> : null}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <HoldList holds={data.holds} canRelease={isSuper} />
      </div>
    </Section>
  );
}

function HoldList({ holds, canRelease }: { holds: Hold[]; canRelease: boolean }) {
  const [releasing, setReleasing] = useState<string | null>(null);
  const m = useAdminMutation((input: { id: string; reason: string }) => adminPost.releaseHold(input.id, input.reason));
  if (holds.length === 0)
    return <p className="text-sm text-muted">No hay campañas detenidas por control financiero.</p>;
  return (
    <div className="grid gap-2">
      <h3 className="text-sm font-medium">Detenidas por control financiero</h3>
      <ul className="grid gap-2">
        {holds.map((h) => (
          <li key={h.id} className="rounded-md border border-line p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                <strong>{h.company}</strong>
                {h.campaign ? ` · ${h.campaign}` : " · toda la empresa"} — {HOLD_SOURCE[h.source] ?? h.reason}
              </span>
              <span className="text-xs text-muted">{formatDateTime(h.createdAt)}</span>
            </div>
            {canRelease && h.source === "credit_limit" ? (
              releasing === h.id ? (
                <form
                  className="mt-2 flex flex-wrap items-end gap-2"
                  onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    const reason = formField(new FormData(event.currentTarget), "reason").trim();
                    m.mutate(
                      { id: h.id, reason },
                      {
                        onSuccess: () => {
                          toast.success("Hold liberado.");
                          setReleasing(null);
                        },
                        onError: (e) => toast.error(problemText(e)),
                      }
                    );
                  }}
                >
                  <Field label="Motivo de la liberación">
                    <Input name="reason" minLength={3} maxLength={500} required />
                  </Field>
                  <Button type="submit" variant="primary" disabled={m.isPending}>
                    Liberar
                  </Button>
                </form>
              ) : (
                <Button
                  className="mt-2"
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setReleasing(h.id);
                  }}
                >
                  Liberar (requiere que el pendiente esté bajo el margen)
                </Button>
              )
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PolicyForm({ policy }: { policy: Policy }) {
  const [open, setOpen] = useState(false);
  const m = useAdminMutation((body: Parameters<typeof adminPost.creditPolicy>[0]) => adminPost.creditPolicy(body));
  if (!open)
    return (
      <div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setOpen(true);
          }}
        >
          Cambiar márgenes
        </Button>
      </div>
    );
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    const bps = (name: string) => Math.round(Number(formField(f, name)) * 100);
    m.mutate(
      {
        orgSafetyMarginBps: bps("org"),
        campaignSafetyMarginBps: bps("campaign"),
        warningThresholdBps: bps("warning"),
        consumptionMaxAgeMinutes: Number(formField(f, "maxAge")),
        reason: formField(f, "reason").trim(),
      },
      {
        onSuccess: () => {
          toast.success("Política actualizada.");
          setOpen(false);
        },
        onError: (e) => toast.error(problemText(e)),
      }
    );
  };
  return (
    <form className="grid max-w-xl gap-3 sm:grid-cols-2" onSubmit={submit}>
      <Field label="Margen de la empresa (%)">
        <Input name="org" type="number" min={0} max={50} step="0.5" defaultValue={policy.org_safety_margin_bps / 100} />
      </Field>
      <Field label="Margen por campaña (%)">
        <Input
          name="campaign"
          type="number"
          min={0}
          max={50}
          step="0.5"
          defaultValue={policy.campaign_safety_margin_bps / 100}
        />
      </Field>
      <Field label="Aviso al (%)">
        <Input
          name="warning"
          type="number"
          min={10}
          max={99}
          step="1"
          defaultValue={policy.warning_threshold_bps / 100}
        />
      </Field>
      <Field label="Lectura vigente (minutos)">
        <Input name="maxAge" type="number" min={5} max={10080} defaultValue={policy.consumption_max_age_minutes} />
      </Field>
      <Field label="Motivo del cambio" className="sm:col-span-2">
        <Input name="reason" minLength={3} maxLength={300} required />
      </Field>
      <div className="flex gap-2 sm:col-span-2">
        <Button type="submit" variant="primary" disabled={m.isPending}>
          Guardar política
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setOpen(false);
          }}
        >
          Cancelar
        </Button>
      </div>
    </form>
  );
}
